<template>
  <div class="sgo-page">
    <header class="sgo-header">
      <span class="sgo-header-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
          <path
            d="M20 6h-2.18A3 3 0 0 0 15 4H9a3 3 0 0 0-2.82 2H4a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2zm-11-.5h6a1.5 1.5 0 0 1 0 3H9a1.5 1.5 0 0 1 0-3zM4 19V8h2.18A3 3 0 0 0 9 9.5h6A3 3 0 0 0 17.82 8H20v11H4z"
          />
        </svg>
      </span>
      <h1 class="sgo-header-title">商品详情</h1>
    </header>

    <div v-if="loading" class="sgo-card sgo-loading">加载中…</div>

    <div v-else class="sgo-card">
      <div class="sgo-media">
        <img v-if="detail.src" :src="detail.src" :alt="detail.title" class="sgo-img" />
        <div v-else class="sgo-placeholder" :class="`sgo-placeholder--${variant}`">
          <template v-if="variant === 'return'">
            <div class="sgo-shield">
              <span>退</span>
            </div>
          </template>
          <template v-else>
            <div class="sgo-pickup-mark">
              <span class="sgo-pickup-cn">提货</span>
              <span class="sgo-pickup-en">tihuo</span>
              <span class="sgo-pickup-tip">请勿乱拍</span>
            </div>
          </template>
        </div>
      </div>

      <h2 class="sgo-title">{{ detail.title }}</h2>
      <p class="sgo-unit">单价: {{ formatUnit(unitPrice) }} USDT</p>
      <p class="sgo-stock">库存: {{ detail.count }}</p>
      <p class="sgo-total">总金额: {{ formatTotal(totalAmount) }} USDT</p>

      <div class="sgo-field">
        <label class="sgo-label">联系人:</label>
        <input
          v-model="contact"
          class="sgo-input"
          type="text"
          placeholder="请输入 联系方式"
          autocomplete="off"
        />
      </div>

      <div class="sgo-field sgo-qty-field">
        <label class="sgo-label">
          <span class="sgo-cart" aria-hidden="true">🛒</span>
          购买数量:
        </label>
        <div class="sgo-qty">
          <button type="button" class="sgo-qty-btn" :disabled="qty <= 1" @click="decQty">−</button>
          <input
            v-model="qtyText"
            class="sgo-qty-input"
            type="text"
            inputmode="numeric"
            maxlength="6"
            @blur="normalizeQty"
          />
          <button type="button" class="sgo-qty-btn" @click="incQty">+</button>
        </div>
      </div>

      <button type="button" class="sgo-pay" :disabled="ordering" @click="onPay">
        <span class="sgo-pay-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <path
              d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 4H4V6h16v2zm0 10H4v-6h16v6z"
            />
          </svg>
        </span>
        {{ ordering ? '提交中...' : '去支付' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import { addOrder } from '../util/api';
import { resolveReferralContext } from '../util/deeplink';
import { makeOrderNo, pushSelectWallet } from '../util/paymentFlow';
import { loadSpecialProduct, resolveLinkKey } from '../util/specialProduct';

const PAGE_DEFAULTS = {
  pickup1: {
    title: '提货登记-请与客服沟通好再下单',
    price: 1,
    count: 9982,
    variant: 'pickup',
  },
  'pickup0.1': {
    title: '提货登记-请与客服沟通好再下单',
    price: 0.1,
    count: 9982,
    variant: 'pickup',
  },
  return1: {
    title: '退货退款请与客服沟通清楚再下单',
    price: 1,
    count: 0,
    variant: 'return',
  },
  'return0.1': {
    title: '退货退款请与客服沟通清楚再下单',
    price: 0.1,
    count: 0,
    variant: 'return',
  },
};

function resolvePageVariant(key, routeMetaVariant) {
  if (String(key || '').startsWith('return')) return 'return';
  if (String(key || '').startsWith('pickup')) return 'pickup';
  return routeMetaVariant || 'pickup';
}

function resolvePageDefaults(key, routeMeta) {
  if (PAGE_DEFAULTS[key]) return PAGE_DEFAULTS[key];
  const variant = resolvePageVariant(key, routeMeta?.orderVariant);
  if (variant === 'return') return PAGE_DEFAULTS.return1;
  return PAGE_DEFAULTS.pickup1;
}

const route = useRoute();
const router = useRouter();

const loading = ref(true);
const ordering = ref(false);
const contact = ref('');
const qty = ref(1);
const qtyText = ref('1');
const referral_code = ref('');
const gameGroupId = ref('');
const linkKey = ref('pickup1');
const detail = ref({
  title: '',
  price: 1,
  count: 0,
  src: '',
});

const variant = computed(() => resolvePageVariant(linkKey.value, route.meta?.orderVariant));

const unitPrice = computed(() => {
  const n = Number(detail.value.price);
  return Number.isFinite(n) && n >= 0 ? n : 0;
});

const totalAmount = computed(() => {
  const q = Math.max(1, Number(qty.value) || 1);
  return unitPrice.value * q;
});

function formatUnit(n) {
  if (!Number.isFinite(n)) return '0';
  if (Number.isInteger(n)) return `${n}.0`;
  return String(Number(n.toFixed(4)));
}

function formatTotal(n) {
  if (!Number.isFinite(n)) return '0.00';
  return (Math.round(n * 100) / 100).toFixed(2);
}

function syncReferral() {
  const ctx = resolveReferralContext(route);
  referral_code.value = ctx.referralCode;
  gameGroupId.value = ctx.gameGroupId;
}

function incQty() {
  qty.value = Math.min(999999, (Number(qty.value) || 1) + 1);
  qtyText.value = String(qty.value);
}

function decQty() {
  qty.value = Math.max(1, (Number(qty.value) || 1) - 1);
  qtyText.value = String(qty.value);
}

function normalizeQty() {
  const n = parseInt(String(qtyText.value).replace(/\D/g, ''), 10);
  qty.value = Number.isFinite(n) && n > 0 ? n : 1;
  qtyText.value = String(qty.value);
}

watch(qtyText, (v) => {
  const cleaned = String(v || '').replace(/\D/g, '');
  if (cleaned !== v) qtyText.value = cleaned;
});

async function loadPage() {
  loading.value = true;
  syncReferral();
  const key =
    resolveLinkKey(route) ||
    route.meta?.linkKey ||
    'pickup1';
  linkKey.value = key;
  const defaults = resolvePageDefaults(key, route.meta);

  try {
    const product = await loadSpecialProduct(key, {
      title: defaults.title,
      price: defaults.price,
      count: defaults.count,
      src: '',
    });
    detail.value = {
      title: product.title || defaults.title,
      price: product.configured ? Number(product.price) : defaults.price,
      count: product.count ?? defaults.count,
      src: product.src || '',
    };
  } catch (e) {
    console.warn('[SpecialGoodsOrder] load failed', e);
    detail.value = { ...defaults, src: '' };
  } finally {
    loading.value = false;
  }
}

async function onPay() {
  if (ordering.value) return;
  const contactVal = contact.value.trim();
  // 联系方式可选；需要强制时恢复下方校验
  // if (!contactVal) {
  //   message.warning('请输入联系方式');
  //   return;
  // }
  normalizeQty();
  if (unitPrice.value < 0) {
    message.warning('价格无效');
    return;
  }

  const data = {
    price: unitPrice.value,
    inputNumber: qty.value,
    inputGT: contactVal,
    orderNo: makeOrderNo(),
    title: detail.value.title || (resolvePageDefaults(linkKey.value, route.meta)?.title ?? '商品'),
    referral_code: referral_code.value,
  };

  ordering.value = true;
  try {
    await addOrder(data);
    pushSelectWallet(router, data, gameGroupId.value);
  } catch (e) {
    console.error(e);
    message.error('下单失败，请稍后重试');
  } finally {
    ordering.value = false;
  }
}

onMounted(loadPage);
watch(() => route.fullPath, loadPage);
</script>

<style scoped>
.sgo-page {
  min-height: 100vh;
  min-height: 100dvh;
  background: #f0f2f5;
  padding: max(16px, env(safe-area-inset-top)) 16px max(24px, env(safe-area-inset-bottom));
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial,
    'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}

.sgo-header {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 480px;
  margin: 0 auto 14px;
  color: #1a2744;
}

.sgo-header-icon {
  display: inline-flex;
  color: #1a2744;
}

.sgo-header-title {
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0.5px;
}

.sgo-card {
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 8px 28px rgba(26, 39, 68, 0.08);
  padding: 22px 20px 26px;
  box-sizing: border-box;
  text-align: center;
}

.sgo-loading {
  color: #888;
  padding: 48px 20px;
}

.sgo-media {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.sgo-img {
  width: min(220px, 58vw);
  height: min(220px, 58vw);
  object-fit: cover;
  border-radius: 8px;
  background: #f5f5f5;
}

.sgo-placeholder {
  width: min(220px, 58vw);
  height: min(220px, 58vw);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sgo-placeholder--return {
  background: #e8e8e8;
}

.sgo-placeholder--pickup {
  background: #111;
}

.sgo-shield {
  width: 88px;
  height: 96px;
  background: #1a1a1a;
  clip-path: polygon(50% 0%, 100% 18%, 100% 62%, 50% 100%, 0% 62%, 0% 18%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 36px;
  font-weight: 900;
}

.sgo-pickup-mark {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: #d4af37;
}

.sgo-pickup-cn {
  font-size: 42px;
  font-weight: 900;
  letter-spacing: 4px;
  line-height: 1;
}

.sgo-pickup-en {
  writing-mode: vertical-rl;
  font-size: 12px;
  letter-spacing: 2px;
  opacity: 0.85;
  position: absolute;
}

.sgo-pickup-mark {
  position: relative;
}

.sgo-pickup-en {
  position: absolute;
  right: -18px;
  top: 8px;
}

.sgo-pickup-tip {
  margin-top: 18px;
  font-size: 12px;
  color: rgba(212, 175, 55, 0.9);
}

.sgo-title {
  margin: 0 0 12px;
  font-size: 17px;
  font-weight: 800;
  color: #1f2a37;
  line-height: 1.45;
}

.sgo-unit {
  margin: 0 0 6px;
  color: #e53935;
  font-size: 16px;
  font-weight: 700;
}

.sgo-stock {
  margin: 0 0 10px;
  color: #9aa3af;
  font-size: 13px;
}

.sgo-total {
  margin: 0 0 22px;
  color: #22c55e;
  font-size: 20px;
  font-weight: 800;
}

.sgo-field {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  text-align: left;
}

.sgo-label {
  flex: 0 0 auto;
  min-width: 72px;
  font-size: 14px;
  font-weight: 700;
  color: #333;
  white-space: nowrap;
}

.sgo-cart {
  margin-right: 2px;
}

.sgo-input {
  flex: 1;
  min-width: 0;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 15px;
  color: #333;
  outline: none;
  background: #fff;
  box-sizing: border-box;
}

.sgo-input::placeholder {
  color: #c0c4cc;
}

.sgo-input:focus {
  border-color: #409eff;
}

.sgo-qty-field {
  margin-bottom: 22px;
}

.sgo-qty {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.sgo-qty-btn {
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 50%;
  background: #409eff;
  color: #fff;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
}

.sgo-qty-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.sgo-qty-input {
  width: 56px;
  height: 34px;
  text-align: center;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 700;
  color: #333;
  outline: none;
}

.sgo-pay {
  width: 100%;
  height: 48px;
  border: none;
  border-radius: 999px;
  background: #409eff;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  -webkit-tap-highlight-color: transparent;
}

.sgo-pay:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.sgo-pay:active:not(:disabled) {
  background: #2f8de6;
}

.sgo-pay-icon {
  display: inline-flex;
}

@media (max-width: 480px) {
  .sgo-page {
    padding-left: 12px;
    padding-right: 12px;
  }

  .sgo-card {
    padding: 18px 14px 22px;
    border-radius: 12px;
  }

  .sgo-title {
    font-size: 15px;
  }

  .sgo-total {
    font-size: 18px;
  }

  .sgo-field {
    flex-wrap: wrap;
  }

  .sgo-label {
    min-width: auto;
  }

  .sgo-input {
    flex: 1 1 100%;
    font-size: 16px;
  }
}
</style>
